import { Injectable } from '@angular/core';
import { Observable, Subject, ReplaySubject, Subscription } from 'rxjs';

export interface ConnectionStatus {
  lastCallStatus: 'online' | 'offline' | 'crash';
}

/**
 * Espone uno stato influenzato dall'esito dell'ultima
 * chiamata http verso il server che muta in :
 *  * - ONLINE se l'ultima chiamata e' andata in stato ok/200
 * - ONLINE ma CON FALLIMENTO se l'ultima chiamata e'
 * fallita per un errore della chiamata o interno al 
 * server (400 o 500)
 * - OFFLINE non e' stato possibile contattare il server
 * 
 * I flag pubblicamente esposti possono essere usati dagli altri
 * componenti per decidere se fare o meno chiamate al server
 * 
 * Si appoggia all'API Html5
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Network_Information_API 
 * @see https://capacitor.ionicframework.com/docs/apis/network
 */
@Injectable({
  providedIn: 'root'
})
export class NetworkService {
  private _connectionStatus: ConnectionStatus;
  private internalSubscription: Subscription;
  private connectionSubject: Subject<ConnectionStatus>;

  constructor() {
    this.initializeObservables();
  }

  private async initializeObservables() {
    this.connectionSubject = new ReplaySubject<ConnectionStatus>(1);
    this.internalSubscription = this.connectionSubject.subscribe(val => this._connectionStatus = val);
    await this.initializeStartupNetworkStatus();
  }

  private async initializeStartupNetworkStatus() {
    console.debug('Applicazione caricata con connessione offline');
    const newConnectionStatus: ConnectionStatus = {
      lastCallStatus: 'offline',
    };
    // Le future chiamate, tra cui il login, riportano online lo stato eventualmente
    this.connectionSubject.next(newConnectionStatus);
  }

  get currentConnectionStatusObservable(): Observable<ConnectionStatus> {
    return this.connectionSubject.asObservable();
  }

  /**
   * L'ultima richiesta e' andata a buon fine
   * (il server ha risposto e con uno stato ok/200 )
   */
  setOnline() {
    if (!(this._connectionStatus.lastCallStatus === 'online')) {
      const newConnectionStatus: ConnectionStatus = {
        lastCallStatus: 'online',
      };
      this.connectionSubject.next(newConnectionStatus);
    }
  }

  /**
   * Non e' stato possibile contattare il server
   * Stato 0
   */
  setOffline() {
    if (!(this._connectionStatus.lastCallStatus === 'offline')) {
      const newConnectionStatus: ConnectionStatus = {
        lastCallStatus: 'offline',
      };
      this.connectionSubject.next(newConnectionStatus);
    }
  }

  /**
   * Il server ha rifiutato la richiesta (stato 400)
   * oppure ha un errore interno (stato 500)
   */
  setLastRequestFailure() {
    if (!(this._connectionStatus.lastCallStatus === 'crash')) {
      const newConnectionStatus: ConnectionStatus = {
        lastCallStatus: 'crash',
      };
      this.connectionSubject.next(newConnectionStatus);
    }
  }

}
